import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useRef, useState } from 'react'
import black from '../../../../art/brand/thananan-icon-black.svg?raw'
import orange from '../../../../art/brand/thananan-icon-orange.svg?raw'
import classes from './QrCode.module.css'

/** The brand's tile with a white ring around it, so the code's dots stop short of the tile instead of touching it */
const ringed = (tile: string) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">${tile.replace('<svg ', '<svg x="12" y="12" width="76" height="76" ')}</svg>`,
  )}`
// Orange on a screen; black on paper, where nothing is filled with colour
const marks = { screen: ringed(orange), paper: ringed(black) }
/** How much of the code's width the T takes, ring included: about 6% of its dots, well inside what level H restores (30%) */
const MARK_SIZE = 0.24

export interface QrCodeProps {
  /** What scanning it opens */
  value: string
  /** At its top-left: the document it belongs to, or what scanning it does */
  label: string
  /** A line of its own under the label, never broken: the document's number, whose screen or invite it is */
  detail?: string
  /** Its width in px; a page may stretch it with CSS, the T and the label's width follow */
  size: number
  /** On a printed document: bare on the paper, the label in points, the T on black */
  paper?: boolean
  /** The page's own look on top: every rule here gives way to a single class */
  className?: string
}

/**
 * Every QR the system shows or prints, one look: its label at the top-left, the company's T in the middle, dark on
 * white in either theme (a white card on screen, its padding the quiet zone). Nothing else draws a QR (biome.json).
 *
 * A browser saves no picture of an inline SVG, so on screen the card is also drawn as a PNG, laid over it unseen:
 * right-click "Save image" or "Copy image", or a long press on a phone, keeps the whole card, label and T included.
 */
export function QrCode({ value, label, detail, size, paper, className }: QrCodeProps) {
  const card = useRef<HTMLElement>(null)
  const [picture, setPicture] = useState<string>()
  const title = detail ? `${label} ${detail}` : label

  // biome-ignore lint/correctness/useExhaustiveDependencies: drawn again whenever what the card shows changes
  useEffect(() => {
    const node = card.current
    if (paper || !node) return
    let live = true
    // Once the fonts have loaded, so the picture has them; without a picture the card still shows and scans
    document.fonts.ready
      .then(() => import('html-to-image'))
      // The card alone: not the margin that centres it, nor a page's fading of it
      .then(({ toPng }) =>
        toPng(node, { pixelRatio: 3, style: { margin: '0', opacity: '1' }, filter: (child) => !(child instanceof HTMLImageElement) }),
      )
      .then((png) => live && setPicture(png))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [paper, value, label, detail, size])

  return (
    <figure ref={card} className={className ? `${classes.qr} ${className}` : classes.qr} data-paper={paper || undefined}>
      <figcaption className={classes.label}>
        {label}
        {detail && <span className={classes.detail}>{detail}</span>}
      </figcaption>
      <QRCodeSVG
        value={value}
        size={size}
        level="H"
        marginSize={0}
        title={title}
        imageSettings={{ src: paper ? marks.paper : marks.screen, width: size * MARK_SIZE, height: size * MARK_SIZE, excavate: true }}
      />
      {picture && !paper && <img className={classes.picture} src={picture} alt={title} />}
    </figure>
  )
}
